import { ReactElement } from 'react';

import { BundleCartItem, BundleProduct } from '@storefront/data-commerce';
import dynamic from 'next/dynamic';

import { BASE_URL } from '@storefront/utils/constants';
import { env } from 'next-runtime-env';
import { getProductTypeLabelFromOption } from './prodRequests';
import { getWebAnalyticsModule } from './webAnalytics';
import { BundleProductWithLowestPrice } from '@storefront/app/pdp-config/printer/product-configuration/product-configuration.utils';
import { getCurrencySymbol } from './priceHandler';
import currency from 'currency.js';

const webAnalytics = getWebAnalyticsModule();

export const ONE_SECOND_IN_MS = 1 * 1000;
export const SLIDES_PER_VIEW = 1.25;
export const ONE_DAY_IN_S = 24 * 60 * 60;
export const ONE_DAY_IN_MS = ONE_SECOND_IN_MS * 60 * 60 * 24;
export const PAPER_NOT_ELIGIBLE = 'PAPER_NOT_ELIGIBLE';

export const emptyOrFalsyObject = (
  objectToCheck: Record<string, unknown>
): boolean => {
  if (!objectToCheck) return true;
  return Object.keys(objectToCheck).length === 0;
};

export const isMobile = () => {
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  ) {
    return true;
  } else {
    return false;
  }
};

export const isHPItgLive = () => {
  const appEnv = env('NEXT_PUBLIC_APP_ENV');
  return (
    window.location.host.startsWith('itg-live.www.hp.com') && appEnv === 'stg'
  );
};

type ActionFunction = (args?: any) => void;

export const checkKeysPressed = (
  event: React.KeyboardEvent<unknown>,
  action: ActionFunction,
  args: any
) => {
  if (event.code === 'Enter' || event.code === 'Space') {
    event.preventDefault();
    action(args);
  }
};

export const decodeToken = (
  token: string
): {
  tenant_id: string;
} => {
  if (!token) {
    return null;
  }
  return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
};

export const getVisitUUID = () => {
  try {
    const visitUuid = webAnalytics?.getVisitUUID();
    if (!visitUuid) {
      console.error(
        'web-analytics-bundle not available, visitUuid will be empty.'
      );
      return '';
    }
    return visitUuid;
  } catch (error) {
    console.error(
      'web-analytics-bundle not available, visitUuid will be empty.',
      error
    );
    return '';
  }
};

export const getOptInGroups = () => {
  if (typeof window !== 'undefined' && (window as any)?._O) {
    return (window as any)?._O?.getOptInGroups();
  }
  return '';
};

export const generateThreatMetrixSessionId = (cartId: string) => {
  const rand = crypto.getRandomValues(new Uint32Array(1))[0] % 10000;
  const nonce = Date.now() + '_' + rand;
  return cartId + '_' + nonce;
};

export const clearCookies = (keepCookies: string[]) => {
  const rootDomain = '.' + window.location.hostname.replace(/^[^.]+\./, '');

  document.cookie.split(';').forEach((cookie) => {
    const [name, _] = cookie.split('=');
    const cookieName = name.trim();
    if (!keepCookies.includes(cookieName)) {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${rootDomain}`;
    }
  });
};

export type PerformanceType = {
  time: number;
  identifier?: string;
};

export const getLocalStorage = (key: string) => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(key);
  }
  return undefined;
};

/*
  let triggeredPerformace= [] as Array<PerformanceType>
  triggeredPerformace.push({
    time: performance.now(),
    identifier: '===begin'
  })
  code to check performance
  triggeredPerformace.push({
    time: performance.now(),
    identifier: '===end'
  })
  checkPerformance(triggeredPerformace)
*/
export const checkPerformance = (
  triggeredPerformace: Array<PerformanceType>
) => {
  for (let index = 0; index < triggeredPerformace.length; index++) {
    if (index === triggeredPerformace.length - 1) {
      const lastTime = triggeredPerformace[triggeredPerformace.length - 1].time;
      const firstTime = triggeredPerformace[0].time;
      const totalTimeValue = lastTime - firstTime;
      console.log(`Total call time ${totalTimeValue} milliseconds.`);
    } else {
      const nextTime = triggeredPerformace[index + 1].time;
      const previousTime = triggeredPerformace[index].time;
      const previousId = triggeredPerformace[index]?.identifier;
      const nextId = triggeredPerformace[index + 1]?.identifier;
      const timeValue = nextTime - previousTime;
      if (previousId)
        console.log(
          `Call to interval ${previousId} - ${nextId} took ${timeValue} milliseconds.`
        );
      else
        console.log(
          `Call to interval ${index} took ${timeValue} milliseconds.`
        );
    }
  }
};
type ReactComponent = {
  default: React.ComponentType<any>; // Replace `any` with specific props type if known
};

type AddLoadingSkeleton = {
  importedComponentCallback: () => Promise<ReactComponent>;
  loadingSkeleton: ReactElement;
  isSsr?: boolean;
};
export const addLoadingSkeleton = ({
  importedComponentCallback,
  loadingSkeleton,
  isSsr = false,
}: AddLoadingSkeleton) =>
  dynamic(importedComponentCallback, {
    ssr: isSsr,
    loading: () => loadingSkeleton,
  });

export const getCurrentPage = (pathname: string) => {
  if (!pathname) return '';
  const pathArray = pathname.split('/');
  const page = pathArray[pathArray.length - 1];
  const words = page.split('-');
  const capitalizedWords = words.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1)
  );
  return capitalizedWords.join('');
};

export const isProductInstantPaper = (multipleProduct: BundleProduct[]) =>
  multipleProduct?.some((product) =>
    product?.items?.some((item) =>
      item?.options?.some(
        (option) => getProductTypeLabelFromOption(option) === 'instantPaper'
      )
    )
  );

export const isProductInkTank = (multipleProduct: BundleProduct[]) =>
  multipleProduct.some((product) =>
    product?.items?.some((item) =>
      item?.options?.some(
        (option) => option.product.hp_supply_type_label === 'inkTank'
      )
    )
  );

export const hasPrinterProduct = (cartItems: BundleCartItem[]) =>
  cartItems.some((item) =>
    (item?.product as BundleProduct).items?.some((productItem) =>
      productItem?.options?.some(
        (option) => option?.product?.hp_product_type_label === 'printer'
      )
    )
  );

export const getPrinterProduct = (cartItems: BundleCartItem[]) =>
  cartItems.find((item) =>
    (item?.product as BundleProduct).items?.some((productItem) =>
      productItem?.options?.some(
        (option) => option?.product?.hp_product_type_label === 'printer'
      )
    )
  )?.product;

export const buildPath = (relativePath: string) =>
  `${BASE_URL}/${relativePath.replace(/^\//, '')}`;

export const getIconPath = (iconName: string) =>
  buildPath(`/icons/${iconName}.svg`);

export function parseUrl(url?: string): {
  path: string;
  params: Record<string, string>;
} {
  const [path, queryString] = (url ?? '').split('?');
  const params: Record<string, string> = {};
  if (queryString) {
    const searchParams = new URLSearchParams(queryString);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
  }
  return { path, params };
}

/**
 * Creates a popstate event handler that prevents browser back navigation
 * and triggers a custom navigation action from flow orchestrator.
 *
 * @param goToPreviousStep - Function to call when browser back is triggered
 * @param getBackUrlParts - Handles the path and parameters to pass to goToPreviousStep
 * @returns Object with setup and cleanup functions
 */
export function createPopstateHandler(
  goToPreviousStep: (options: {
    path?: string;
    params?: Record<string, string | undefined>;
    shouldUseRouter?: boolean;
  }) => void,
  getBackUrlParts: () => {
    path?: string;
    params: Record<string, string | undefined>;
  }
) {
  const handlePopState = (event: PopStateEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // Push the current state again to prevent actual navigation
    history.pushState(null, document.title);

    const { path, params } = getBackUrlParts();
    goToPreviousStep({ path, params, shouldUseRouter: false });
  };

  const setup = () => {
    // Push the current state again to prevent actual navigation and adds an event listener
    history.pushState(null, document.title);
    window.addEventListener('popstate', handlePopState);
  };

  const cleanup = () => {
    // remove the event listener
    window.removeEventListener('popstate', handlePopState);
  };

  return { setup, cleanup };
}
export const getProductsLowestPrice = (
  products: BundleProductWithLowestPrice[]
): number => {
  const initialProduct = products[0];

  if (!initialProduct) {
    return 0;
  }

  return products.reduce(
    (minProduct, currProduct) =>
      minProduct.lowest_price <= currProduct.lowest_price
        ? minProduct
        : currProduct,
    initialProduct
  ).lowest_price;
};

export interface PriceFormatterOptions {
  selectedOptions?: any[];
  totalPricePerMonth?: number;
  products?: BundleProductWithLowestPrice[];
  currencyCode: string;
  pattern?: string;
  precision?: number;
}

export const formatPrice = ({
  selectedOptions,
  totalPricePerMonth,
  products,
  currencyCode,
  pattern = '!#',
  precision = 2,
}: PriceFormatterOptions): string => {
  const currencySymbol = getCurrencySymbol(currencyCode);

  if (selectedOptions?.length > 0 && totalPricePerMonth !== undefined) {
    return currency(totalPricePerMonth, {
      pattern,
      symbol: currencySymbol,
      precision,
    }).format();
  } else if (products) {
    return currency(getProductsLowestPrice(products), {
      pattern,
      symbol: currencySymbol,
      precision,
    }).format();
  }

  return currency(0, {
    pattern,
    symbol: currencySymbol,
    precision,
  }).format();
};
